import Lights from "./Lights";
import WaveFunctionCollapse from "./WaveFunctionCollapseV2/WaveFunctionCollapse";
import Vector3 from "./WaveFunctionCollapseV2/Utils/Vector3";
import { Grid, OrbitControls } from "@react-three/drei";
import PrototypeManager from "./WaveFunctionCollapseV2/Prototype/PrototypeManager";
import { button, useControls } from "leva";
import World from "./World/World.tsx";
import PrototypeManagerImpl from "./PrototypeManagerImpl.ts";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import PrototypeInput from "./WaveFunctionCollapseV2/Prototype/PrototypeInput.ts";
import Cell from "./WaveFunctionCollapseV2/Cell.ts";
import * as THREE from "three";
import DebugWorld from "./World/DebugWorld.tsx";
import { useDebug } from "./Resources/DebugProvider.tsx";
import { useThree } from "@react-three/fiber";
import { useEditorStore } from "./Editor/stores/editorStore.ts";
import EditorCell from "./Editor/EditorCell.tsx";
import PrototypeImpl from "./PrototypeImpl.ts";
import EditorPrototype from "./Editor/EditorPrototype.tsx";

// Ground
const boxGeometry = new THREE.BoxGeometry(1, 1, 1);
const floorMaterial = new THREE.MeshStandardMaterial({
  color: "#ffffff",
});

// const MAP_SIZE: Vector3 = new Vector3(15, 10, 15);
const MAP_SIZE: Vector3 = new Vector3(10, 4, 10);
// const MAP_SIZE: Vector3 = new Vector3(1, 1, 1);

export default function WfcExperience() {
  const [prototypeManager, setPrototypeManager] = useState<PrototypeManager>();
  const [WFC, setWFC] = useState<WaveFunctionCollapse>();

  const [map, setMap] = useState<Cell[][][]>();

  const debug = useDebug().debug;

  const { camera } = useThree();

  useEffect(() => {
    const fetchData = async (): Promise<PrototypeInput> => {
      return await PrototypeManagerImpl.loadPrototypeInput(
        // @ts-ignore
        import.meta.env.VITE_PROTOTYPE_DATA_PATH,
      );
    };

    fetchData()
      .then((prototypeInput: PrototypeInput) => {
        const manager = new PrototypeManagerImpl(prototypeInput);
        setPrototypeManager(manager);
        setWFC(new WaveFunctionCollapse(manager, MAP_SIZE, false));
      })
      .catch((error) => {
        console.error("Error while fetching prototype data", error);
      });
  }, []);

  // FOR EDITOR
  const setEditorMap = useEditorStore((state) => state.setMap);

  const setMapInEditor = (map: Cell[][][]) => {
    const editorMap: EditorCell[][][] = map.map((x, xIndex) =>
      x.map((y, yIndex) =>
        y.map((z, zIndex) => {
          if (z.collapsed && z.collapsedCellPrototypeId) {
            const prototype = prototypeManager!.getPrototypeById(
              z.collapsedCellPrototypeId,
            ) as PrototypeImpl;

            const editorCell = new EditorCell(
              new THREE.Vector3(xIndex, yIndex, zIndex),
            );

            if (
              prototype &&
              prototype.mesh_name !== "-1" /*TODO : hard coded value*/
            ) {
              editorCell.prototype = {
                id: prototype.id,
                mesh_name: prototype.mesh_name,
                mesh_rotation: prototype.mesh_rotation,
              } as EditorPrototype;
            }

            return editorCell;
          } else {
            return new EditorCell(new THREE.Vector3(xIndex, yIndex, zIndex));
          }
        }),
      ),
    );

    setEditorMap(editorMap);
  };

  // Debug controls
  const { cellSize, showGrid, showGround, showEmptyCells, skipEmptyCells } =
    useControls(
      {
        "Start WFC (instant)": button(() => {
          if (WFC) {
            WFC.reset();
            const map = WFC.start();
            setMap([...map]); // TODO : Investigate why not just output
            setMapInEditor(map);
          } else {
            console.log("WFC is not initialized");
          }
        }),
        "Start WFC (auto animated)": button(async () => {
          if (WFC) {
            if (WFC.isFinished()) {
              WFC.reset();
            }
            while (!WFC.isFinished()) {
              const { map, collapsedCell } = WFC.startStepIteration();

              // If skipEmptyCells is enabled, skip the empty cells rendering step
              if (
                skipEmptyCells &&
                collapsedCell.collapsedCellPrototypeId ===
                  prototypeManager!.blankId
              ) {
                continue;
              }

              setMap([...map]); // TODO : Investigate why not just map
              setMapInEditor(map);
              await new Promise((resolve) => setTimeout(resolve, 0)); // Allow UI to update
            }
          } else {
            console.log("WFC is not initialized");
          }
        }),
        skipEmptyCells: true,

        "Start WFC (step by step)": button(() => {
          if (WFC) {
            if (WFC.isFinished()) {
              WFC.reset();
            }
            const { map } = WFC.startStepIteration();
            setMap([...map]); // TODO : Investigate why not just map
            setMapInEditor(map);
          } else {
            console.log("WFC is not initialized");
          }
        }),
        showEmptyCells: false,
        "Reset WFC": button(() => {
          if (WFC) {
            WFC.reset();
            setMap([]);
          } else {
            console.log("WFC is not initialized");
          }
        }),
        cellSize: 2,
        showGrid: false,
        showGround: true,
      },
      [WFC],
    );

  const center = new THREE.Vector3(
    (MAP_SIZE.x / 2) * cellSize - cellSize,
    0,
    (MAP_SIZE.z / 2) * cellSize - cellSize,
  );

  const orbitControlsRef: MutableRefObject<any> = useRef<any>(null);

  // Set the center of the camera and orbit controls to the center of the map if not in debug mode
  useEffect(() => {
    const cameraOffset =
      Math.max(MAP_SIZE.x, MAP_SIZE.y, MAP_SIZE.z) * cellSize + 10;
    if (!debug) {
      camera.position.set(
        center.x + cameraOffset,
        center.y + cameraOffset,
        center.z + cameraOffset,
      );
      orbitControlsRef.current.target = center;
    }
  }, []);

  return (
    <>
      <OrbitControls makeDefault ref={orbitControlsRef} />
      <color args={["#ffffff"]} attach="background" />
      <Lights />

      {!debug && (
        <>
          {showGround && (
            <mesh
              geometry={boxGeometry}
              material={floorMaterial}
              position={center}
              scale={[
                MAP_SIZE.x * cellSize - 0.05, // - 0.05 to prevent z-fighting between grid and ground
                0.2,
                MAP_SIZE.z * cellSize - 0.05, // - 0.05 to prevent z-fighting between grid and ground
              ]}
              receiveShadow
            />
          )}
          {showGrid && (
            <Grid
              args={[cellSize * 10, cellSize * 10]}
              cellSize={cellSize}
              cellThickness={2}
              cellColor={"#ffffff"}
              sectionSize={cellSize * 2}
              sectionThickness={3}
              sectionColor={"#969696"}
              infiniteGrid={true}
              fadeDistance={100}
              followCamera={true}
              position={[cellSize, 0.12, cellSize]} // Center the grid
            />
          )}
          {map && prototypeManager ? (
            <World
              prototypeManager={prototypeManager}
              cellSize={cellSize}
              gridMap={map}
              showEmptyCells={showEmptyCells}
            />
          ) : null}
        </>
      )}

      {debug && prototypeManager ? (
        <DebugWorld cellSize={cellSize} prototypeManager={prototypeManager} />
      ) : null}
    </>
  );
}
